import { useEffect, useRef } from 'react';

// Offset of the spout tip within the 80x80 SVG viewBox (used for smoke origin)
const SPOUT_TIP_X = 6;
const SPOUT_TIP_Y = 22;
// SVG rendered size
const SVG_W = 72;
const SVG_H = 72;
// How the SVG is positioned relative to cursor hotspot (spout tip = cursor tip)
const OFFSET_X = -(SPOUT_TIP_X / 80) * SVG_W;
const OFFSET_Y = -(SPOUT_TIP_Y / 80) * SVG_H;

function spawnSmoke(x, y) {
  const count = 3;
  for (let i = 0; i < count; i++) {
    const el = document.createElement('div');
    const size = 10 + Math.random() * 14;
    const driftX = (Math.random() - 0.5) * 28;
    const duration = 900 + Math.random() * 400;
    const delay = i * 80;

    Object.assign(el.style, {
      position: 'fixed',
      left: `${x - size / 2}px`,
      top: `${y - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(200,200,200,0.55) 0%, rgba(180,180,180,0) 70%)',
      pointerEvents: 'none',
      zIndex: 999998,
      animationName: 'smoke-rise',
      animationDuration: `${duration}ms`,
      animationDelay: `${delay}ms`,
      animationTimingFunction: 'ease-out',
      animationFillMode: 'both',
      '--drift': `${driftX}px`,
    });

    document.body.appendChild(el);
    setTimeout(() => el.remove(), duration + delay + 50);
  }
}

export default function GenieCursor() {
  const cursorRef = useRef(null);
  const posRef = useRef({ x: -300, y: -300 });
  const mouseRef = useRef({ x: -300, y: -300 });
  const smokeRef = useRef(null);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);

  useEffect(() => {
    if (isTouch.current) return;

    const styleId = 'genie-smoke-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes smoke-rise {
          0%   { opacity: 0.8; transform: translateY(0) translateX(0) scale(1); }
          100% { opacity: 0;   transform: translateY(-40px) translateX(var(--drift)) scale(2.5); }
        }
      `;
      document.head.appendChild(style);
    }

    const el = cursorRef.current;

    let raf;

    const onMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };

      // Smoke when hovering clickable elements
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const isClickable = target && (
        target.closest('a, button, [role="button"], input, select, textarea, label, [onClick], [tabindex], .product-card, .product-card-arrival') ||
        window.getComputedStyle(target).cursor === 'pointer'
      );

      if (isClickable) {
        const now = Date.now();
        if (!smokeRef.current || now - smokeRef.current > 120) {
          smokeRef.current = now;
          // Spout tip in screen coords
          const spoutX = posRef.current.x + OFFSET_X + (SPOUT_TIP_X / 80) * SVG_W;
          const spoutY = posRef.current.y + OFFSET_Y + (SPOUT_TIP_Y / 80) * SVG_H;
          spawnSmoke(spoutX, spoutY);
        }
      }
    };

    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = () => {
      posRef.current.x = lerp(posRef.current.x, mouseRef.current.x, 0.16);
      posRef.current.y = lerp(posRef.current.y, mouseRef.current.y, 0.16);
      el.style.transform = `translate(${posRef.current.x + OFFSET_X}px, ${posRef.current.y + OFFSET_Y}px)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
      const s = document.getElementById(styleId);
      if (s) s.remove();
    };
  }, []);

  return (
    <>
    <div
      ref={cursorRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 999999,
        willChange: 'transform',
        display: isTouch.current ? 'none' : 'block',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 80 80"
        width={SVG_W}
        height={SVG_H}
        style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}
      >
        <defs>
          <radialGradient id="gc-body" cx="45%" cy="38%" r="62%">
            <stop offset="0%"   stopColor="#fff0a0" />
            <stop offset="40%"  stopColor="#e8a800" />
            <stop offset="100%" stopColor="#7a5000" />
          </radialGradient>
          <radialGradient id="gc-spout" cx="30%" cy="30%" r="70%">
            <stop offset="0%"   stopColor="#ffd966" />
            <stop offset="100%" stopColor="#9a6500" />
          </radialGradient>
          <radialGradient id="gc-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#ffe066" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#ffe066" stopOpacity="0" />
          </radialGradient>
          <filter id="gc-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#7a5000" floodOpacity="0.5" />
          </filter>
        </defs>

        {/* Ambient glow */}
        <ellipse cx="42" cy="54" rx="26" ry="10" fill="url(#gc-glow)" />

        {/* === LAMP BODY === */}
        {/* Main rounded belly */}
        <ellipse
          cx="44" cy="50"
          rx="21" ry="14"
          fill="url(#gc-body)"
          filter="url(#gc-shadow)"
          stroke="#7a5000" strokeWidth="0.7"
        />

        {/* Flat base */}
        <ellipse cx="44" cy="62" rx="13" ry="3.5"
          fill="#c88000" stroke="#7a5000" strokeWidth="0.5" />

        {/* Neck / collar connecting body to lid */}
        <rect x="37" y="36" width="14" height="5" rx="2"
          fill="#d49000" stroke="#7a5000" strokeWidth="0.5" />

        {/* Lid dome */}
        <ellipse cx="44" cy="36" rx="11" ry="4"
          fill="#e8a800" stroke="#7a5000" strokeWidth="0.6" />
        <ellipse cx="44" cy="33" rx="7" ry="5"
          fill="#d49000" stroke="#7a5000" strokeWidth="0.6" />

        {/* Lid knob */}
        <ellipse cx="44" cy="29" rx="3" ry="2"
          fill="#e8a800" stroke="#7a5000" strokeWidth="0.5" />

        {/* === SPOUT ===
            Curves from the left side of body (x≈24,y≈48) up-left to tip (x≈6,y≈22) */}
        <path
          d="M24 48 C18 44, 14 36, 10 28 C8 24, 6 22, 6 22 C10 20, 13 22, 15 26 C18 31, 21 38, 26 44Z"
          fill="url(#gc-spout)"
          stroke="#7a5000" strokeWidth="0.6"
        />

        {/* === HANDLE ===
            Ornate loop on the right */}
        <path
          d="M65 44 C76 44, 76 56, 65 57"
          fill="none"
          stroke="#d49000" strokeWidth="4.5"
          strokeLinecap="round"
        />
        <path
          d="M65 44 C76 44, 76 56, 65 57"
          fill="none"
          stroke="#ffe099" strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.5"
        />

        {/* === ENGRAVINGS / details === */}
        <path d="M32 50 Q44 46 56 50" fill="none" stroke="#ffe099" strokeWidth="0.8" opacity="0.5" />
        <path d="M30 54 Q44 51 58 54" fill="none" stroke="#ffe099" strokeWidth="0.6" opacity="0.35" />

        {/* Shine highlight */}
        <path d="M30 44 Q36 40 43 42" fill="none" stroke="#fffde0" strokeWidth="2" strokeLinecap="round" opacity="0.55" />

        {/* Spout tip dot (hotspot marker — invisible) */}
        {/* x=6 y=22 is the hotspot */}
      </svg>
    </div>
    </>
  );
}
